import { useState, useImperativeHandle, forwardRef, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Loader } from "../../../../components/loader/index.js";
import { toastNotification } from "../../../../services/notification-service.js";
import { addCourseTopic } from "../../../../redux/courses.ts";
import ReactPlayer from "react-player";
import { Upload } from "../../../../components/upload/index.jsx";

const AddUpdateTopic = (props, ref) => {
  const { isLoading } = useSelector((store) => store.course);
  const dispatch = useDispatch();

  const [data, setData] = useState();
  const [show, setShow] = useState(false);
  const [modal, setModal] = useState();
  const [showTimeInput, setShowTimeInput] = useState(false); // New state for PDF time input
  const [uploadProgress, setUploadProgress] = useState(0); // Upload progress state

  useImperativeHandle(ref, () => ({
    open({ chapter, status, topic, course_slug, data_type }) {
      setShow(true);
      setModal(status);
      setUploadProgress(0); // Reset upload progress when opening modal

      if (status === "Update") {
        setData({
          chapter_title: topic?.content_name,
          chapter_id: topic?.content_id,
          title: topic?.title,
          course_slug,
          file: topic?.s3video || topic?.s3audio || topic?.s3document,
          is_audio: !!topic?.module_audio_path,
          is_pdf: !!topic?.module_document_path,
          content_id: topic?.id,
          file_size:
            topic?.module_video_size ||
            topic?.module_audio_size ||
            topic?.module_document_size,
          file_name:
            topic?.module_video_original_name ||
            topic?.module_audio_original_name ||
            topic?.module_document_original_name,
          Video_duration_in_sec: topic?.module_video_duration,
          data_type: data_type,
        });
        if (!!topic?.module_document_path) {
          setShowTimeInput(true);
        }
      } else {
        setData({
          chapter_title: chapter?.content_name,
          chapter_id: chapter?.id,
          course_slug,
          data_type: data_type,
        });
      }
    },
  }));

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const inputThumb = document.getElementById(e.target.id);
    const isVideo = file.type.includes("video");
    const isAudio = file.type.includes("audio");
    const isPDF = file.type === "application/pdf";

    // Restriction based on data_type
    if (data?.data_type !== "TRAINING" && !isVideo) {
      if (inputThumb) inputThumb.value = "";
      return toastNotification({
        status: "warning",
        message: "Only video files are allowed for this course type!",
      });
    }

    if (!isVideo && !isAudio && !isPDF) {
      if (inputThumb) inputThumb.value = "";
      return toastNotification({
        status: "warning",
        message: "Please upload a video, audio, or PDF file only!",
      });
    }

    if (file.size > 100 * 1024 * 1024) {
      if (inputThumb) inputThumb.value = "";
      return toastNotification({
        status: "error",
        message: "File size exceeds the maximum limit of 100 MB!",
      });
    }

    const updatedData = {
      ...data,
      file,
      file_name: file.name,
      file_size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      is_audio: isAudio,
      is_pdf: isPDF,
    };

    if (isVideo) {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        setData({
          ...updatedData,
          Video_duration_in_sec: Math.floor(video.duration),
          is_audio: false,
          is_pdf: false,
        });
        setShowTimeInput(false);
      };
      video.src = URL.createObjectURL(file);
    } else if (isAudio) {
      setData(updatedData);
      setShowTimeInput(false);
    } else if (isPDF) {
      setData(updatedData);
      setShowTimeInput(true);
    }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!data?.file) {
      return toastNotification({
        status: "error",
        message: "Please upload a video/audio/pdf file!",
      });
    }

    if (data?.is_pdf && !data?.Video_duration_in_sec) {
      return toastNotification({
        status: "error",
        message: "Please enter estimated reading time for the PDF!",
      });
    }

    const formData = new FormData();
    const dataToSend = {
      title: data?.title,
      type: "topic",
      course_slug: data?.course_slug,
      chapter_id: data?.chapter_id,
    };

    const isFileString = typeof data.file === "string";
    if (!isFileString) {
      const file = data.file;
      const type = file.type.includes("video")
        ? "video"
        : file.type.includes("audio")
        ? "audio"
        : "pdf";
      dataToSend.file = file;
      dataToSend.file_type = type;
      dataToSend.file_size = data.file_size;
      dataToSend.file_name = data.file_name;
    }

    if (data?.Video_duration_in_sec) {
      formData.append("Video_duration_in_sec", data.Video_duration_in_sec);
    }

    if (data?.content_id) {
      dataToSend["content_id"] = data.content_id;
    }

    Object.keys(dataToSend).forEach((key) => {
      formData.append(key, dataToSend[key]);
    });

    // Reset upload progress
    setUploadProgress(0);

    // Create onUploadProgress callback
    const onUploadProgress = (progressEvent) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
      setUploadProgress(percentCompleted);
    };

    dispatch(addCourseTopic({ formData: { formData } }, setShow, onUploadProgress));
  };

  // Memoize the file URL to prevent recreation on every render
  const fileUrl = useMemo(() => {
    if (!data?.file) return null;
    return typeof data.file === "string"
      ? data.file
      : URL.createObjectURL(data.file);
  }, [data?.file]);

  // Memoize the preview component to prevent re-rendering when uploadProgress changes
  const renderPreview = useMemo(() => {
    if (!fileUrl) return null;

    if (data?.is_audio) {
      return (
        <audio controls className="w-full mt-3">
          <source src={fileUrl} />
        </audio>
      );
    }

    if (data?.is_pdf) {
      return (
        <iframe
          className="w-full mt-3 rounded"
          src={fileUrl}
          width="100%"
          height="500px"
          title="PDF Preview"
        />
      );
    }

    return (
      <div className="w-full mt-3 rounded-lg">
        <ReactPlayer url={fileUrl} width="100%" height="100%" controls />
      </div>
    );
  }, [fileUrl, data?.is_audio, data?.is_pdf]);

  // Cleanup object URL when file changes or component unmounts
  useEffect(() => {
    return () => {
      if (data?.file && typeof data.file !== "string") {
        URL.revokeObjectURL(data.file);
      }
    };
  }, [data?.file]);

  return (
    show && (
      <div
        className="relative z-50"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <form
              onSubmit={handleAdd}
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <label className="block mb-2 font-bold text-center text-orange-600 text-lg">
                  {data?.chapter_title}
                </label>

                <div className="w-full">
                  <label className="block mb-2 font-light text-orange-600">
                    Topic Name *
                  </label>
                  <input
                    value={data?.title || ""}
                    onChange={(e) =>
                      setData({ ...data, title: e.target.value })
                    }
                    type="text"
                    maxLength={100}
                    className="bg-transparent border border-gray-300 text-gray-900 sm:text-sm rounded-lg outline-none focus:border-orange-600 block w-full p-2.5"
                    required
                  />
                </div>

                <div className="w-full mt-3">
                  <label className="block mb-2 font-light text-orange-600">
                    Upload Media File{" "}
                    {data?.data_type === "TRAINING"
                      ? "(Video/Audio/PDF)"
                      : "(Video Only)"}{" "}
                    *
                  </label>

                  <Upload id="file_video_audio">
                    <input
                      id="file_video_audio"
                      type="file"
                      accept={
                        data?.data_type === "TRAINING"
                          ? "video/*, audio/*, application/pdf"
                          : "video/*"
                      }
                      hidden
                      name="media_file"
                      onChange={handleFileChange}
                    />
                  </Upload>

                  {data?.file_name && (
                    <div className="mt-2 text-sm text-gray-700">
                      <div>
                        <strong>File Name:</strong> {data.file_name}
                      </div>
                      <div>
                        <strong>Size:</strong> {data.file_size}
                      </div>
                      {data?.Video_duration_in_sec && !data?.is_pdf && (
                        <div>
                          <strong>Duration:</strong>{" "}
                          {data.Video_duration_in_sec}s
                        </div>
                      )}
                    </div>
                  )}

                  {showTimeInput && data?.is_pdf && (
                    <div className="w-full mt-3">
                      <label className="block mb-2 font-light text-orange-600">
                        Required Study Time (in Minutes) *
                      </label>
                      <input
                        value={
                          data?.Video_duration_in_sec
                            ? Math.floor(data.Video_duration_in_sec / 60)
                            : ""
                        }
                        onChange={(e) =>
                          setData({
                            ...data,
                            Video_duration_in_sec: e.target.value * 60,
                          })
                        }
                        type="number"
                        min="1"
                        className="bg-transparent border border-gray-300 text-gray-900 sm:text-sm rounded-lg outline-none focus:border-orange-600 block w-full p-2.5"
                        required
                      />
                    </div>
                  )}

                  {renderPreview}
                </div>
              </div>

              <div className="bg-transparent px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="submit"
                  className="inline-flex w-full justify-center rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 sm:ml-3 sm:w-auto"
                >
                  {isLoading?.addTopic ? (
                    <Loader w="6" h="6" color="orange" />
                  ) : modal === "Add" ? (
                    "Add"
                  ) : (
                    "Update"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShow(false);
                    setData({});
                    setShowTimeInput(false);
                    setUploadProgress(0);
                  }}
                  className="inline-flex w-full justify-center border border-orange-600 rounded-md bg-white px-3 py-2 text-sm font-semibold text-orange-600 shadow-sm hover:opacity-90 sm:ml-3 sm:w-auto"
                >
                  Back
                </button>
              </div>

              {/* Upload Progress Bar */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="bg-white px-4 pb-4 sm:px-6">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-orange-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <div className="text-center mt-2 text-sm text-gray-600">
                    Uploading... {uploadProgress}%
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    )
  );
};

export default forwardRef(AddUpdateTopic);
